import { ExternalServiceId } from '@edgebox/data-definition-kit';
import { ProgressBar } from './ProgressBar';
import { HealthConfidenceState } from '@edgebox/sync-core-data-definitions';
import { ClientContentItemEntity, ClientContentOptimizationTypeEntity, ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import { Button, Collapse, Form } from '@edgebox/react-components';
import { faArrowRight, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  createCheckOptimization,
  loadActiveOptimizationTypes,
  optimizationTypeKeysByIssueTypeKey as toOptimizationTypeKeysByIssueTypeKey,
  optimizationTypesByKeys,
} from './checkOptimization.client';
import { useOptimizationFollow } from './useOptimizationFollow';
import {
  buildFeatureAssignmentLookup,
  describeConfidenceState,
  GapAssignmentDetail,
  GapIssueEvidence,
  GapTermsInput,
  resolveGapAssignmentDetail,
  toConfidencePercentage,
} from '../Onboarding/ContentInventory/Components/GapAssignmentJoin';
import { HealthCheckNameReference } from '../Onboarding/ContentInventory/Components/HealthCheckNameReference';
import {
  HealthCheckReferenceData,
  HealthCheckRevisionWeight,
  loadHealthCheckReferenceDataByKey,
  loadHealthCheckWeightByRevisionId,
} from '../Onboarding/ContentInventory/Components/HealthCheckReferenceData';
import { getBrandPresenceSubTabClassName } from './BrandPresenceTabStyles';
import { HealthScoreCell } from './HealthScoreCell';
import { BP_TABLE_HEAD_CELL_CLASSES, BP_TABLE_HEAD_CELL_WIDE_CLASSES } from './brandPresenceClasses';
import { GapAssignmentDetails } from './GapAssignmentDetails';
import { GapConfidenceBadge } from './GapConfidenceBadge';
import { HealthScoreShare, toHealthScoreShares } from './healthScoreShares.utils';
import { isSameHealth, toScorePercentage } from './contentHealth.utils';

type GapRow = {
  healthCheckKey: string;
  /** The revision of the check the score was computed with, as the page's figure names it. */
  healthCheckRevisionId?: string;
  score: number | null;
  issueId?: string;
  /** Backend-supplied confidence that the rating is correct (0..1), or null when unknown. */
  confidence: number | null;
  /** How that confidence was determined; drives the badge tooltip copy. */
  confidenceState: HealthConfidenceState | null;
  /** Backend reasoning for the rating, persisted for passing and failing checks. */
  explanation: string | null;
};

/** Which subset of health checks the table shows. */
type HealthFilter = 'gaps' | 'applicable' | 'all' | 'changed';

const HEALTH_FILTERS: { key: HealthFilter; label: string }[] = [
  { key: 'gaps', label: 'Gaps' },
  { key: 'applicable', label: 'Applicable' },
  { key: 'all', label: 'All' },
];

/**
 * While a suggested version is compared, the checks whose health it changes, better or worse: after the views every
 * page has, so those keep their places whether a suggested version is compared or not.
 */
const CHANGED_FILTER: { key: HealthFilter; label: string } = { key: 'changed', label: 'Changed' };

/** Check, Health, First Detected and Potential Gain; Select joins them where optimizing is offered, Weight on demand. */
const ALWAYS_SHOWN_COLUMNS = 4;

/** Where a reader's choice to see each check's weight is remembered, in their own browser. */
const SHOW_WEIGHTS_STORAGE_KEY = 'gaps-showWeights';

/** Whether this reader chose to see each check's weight; off until they do. */
function readShowWeights(): boolean {
  try {
    return window.localStorage.getItem(SHOW_WEIGHTS_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

/** Remembers the reader's choice; a browser that keeps nothing for the frame keeps it for the visit. */
function rememberShowWeights(show: boolean): void {
  try {
    window.localStorage.setItem(SHOW_WEIGHTS_STORAGE_KEY, show ? 'true' : 'false');
  } catch {
    // The choice holds until the frame is reloaded.
  }
}

/** The headings of the columns that read under a check's name where the table is narrow. */
const FIRST_DETECTED_LABEL = 'First Detected';
const POTENTIAL_GAIN_LABEL = 'Potential Gain';

/** What the Weight column says each figure is. */
const WEIGHT_HINT = 'How much each check counts towards the page’s health.';

/** A check's share of the page's health, to one decimal. */
function toShareLabel(share: number): string {
  return `${Math.round(share * 10) / 10}%`;
}

/** What the Weight column says of a check that shows no share, and why, for a reader who points at it. */
const NO_SHARE: Record<Exclude<HealthScoreShare['kind'], 'share'> | 'not-counted', { label: string; reason: string }> = {
  'not-counted': { label: 'Not counted', reason: 'This check has no score on this page, so it does not count towards its health.' },
  loading: { label: 'Loading', reason: 'Shown once the weights of the page’s checks are loaded.' },
  'not-loaded': { label: 'Not loaded', reason: 'The weight of this check could not be loaded. Reload to try again.' },
  'not-named': { label: 'Unknown', reason: 'We can’t tell which version of this check measured the page.' },
};

/** Why a check shows no share: no score on the page, weights still loading, or a weight that could not be loaded. */
function toNoShare(share?: HealthScoreShare): { label: string; reason: string } {
  return NO_SHARE[share && share.kind !== 'share' ? share.kind : 'not-counted'];
}

/**
 * How much a check counts towards the page's health, beside a bar as long as its share relative to the check
 * that counts most, so the checks that weigh most stand out at a glance; where it shows no share, why not.
 */
function HealthScoreShareCell({ share, largestShare }: { share?: HealthScoreShare; largestShare: number }) {
  if (share?.kind !== 'share' || !(largestShare > 0)) {
    const { label, reason } = toNoShare(share);
    return (
      <span className="text-xs text-slate-400" title={reason}>
        {label}
        <span className="sr-only">: {reason}</span>
      </span>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-semibold tabular-nums text-slate-700">{toShareLabel(share.share)}</span>
      <span aria-hidden="true" className="block h-1 w-16 overflow-hidden rounded-full bg-slate-100">
        {/* The width is the share itself, relative to the largest one, known only at runtime. */}
        <span className="block h-full rounded-full bg-slate-400" style={{ width: `${(share.share / largestShare) * 100}%` }} />
      </span>
    </div>
  );
}

/**
 * A check's potential gain, or, while the weights it is weighed by are still loading, that it waits for them, so no
 * figure reads as final while it can still change.
 */
function PotentialGainValue({ gain }: { gain: { label: string; waiting: boolean } }) {
  if (!gain.waiting) {
    return <>{gain.label}</>;
  }

  const { reason } = NO_SHARE.loading;
  return (
    <span className="text-xs font-normal text-slate-400" title={reason}>
      {gain.label}
      <span className="sr-only">: {reason}</span>
    </span>
  );
}

/** A check's share of the page's health under its name, where the table is too narrow for a column of its own. */
function HealthScoreShareLine({ share }: { share?: HealthScoreShare }) {
  const noShare = share?.kind === 'share' ? undefined : toNoShare(share);
  return (
    <div className="mt-1.5 text-xs font-medium text-slate-500 @3xl:hidden" title={noShare?.reason}>
      Weight <span className="tabular-nums text-slate-700">{share?.kind === 'share' ? toShareLabel(share.share) : noShare?.label}</span>
      {noShare ? <span className="sr-only">: {noShare.reason}</span> : null}
    </div>
  );
}

/**
 * The line under the weight switch where the shares leave out checks whose weight is not known: the weights shown
 * and the potential gains are both weighed by those shares.
 */
function toLeftOutLine(leftOut: number): string | undefined {
  if (!leftOut) {
    return undefined;
  }

  return leftOut === 1 ? '1 check without a known weight isn’t included.' : `${leftOut} checks without a known weight aren’t included.`;
}

/**
 * A check's health on the page as it is now and in its suggested version: side by side where the table is wide, one
 * above the other where it is narrow, so both read without scrolling the table sideways. Each is a 0–1 score, shown
 * as a whole percentage. A health whose shown figure the suggested version keeps is shown once; only a change reads
 * as now and suggested.
 */
function HealthComparisonCell({ current, suggested, measured }: { current?: number; suggested?: number; measured: boolean }) {
  if (measured && isSameHealth({ current, suggested })) {
    return <HealthScoreCell score={toScorePercentage(current)} />;
  }

  return (
    <div className="flex flex-col items-start gap-1.5 @3xl:flex-row @3xl:gap-3">
      <div>
        <span className="sr-only">Current version: </span>
        <HealthScoreCell score={toScorePercentage(current)} />
      </div>
      <FontAwesomeIcon icon={faArrowRight} className="rotate-90 text-xs text-slate-400 @3xl:mt-1 @3xl:rotate-0" aria-hidden="true" />
      <div>
        <span className="sr-only">Suggested version: </span>
        {measured ? (
          <HealthScoreCell score={toScorePercentage(suggested)} />
        ) : (
          <span className="text-sm text-slate-500">Not measured yet</span>
        )}
      </div>
    </div>
  );
}

function hasFiniteScore(row: GapRow): boolean {
  return typeof row.score === 'number' && Number.isFinite(row.score);
}

type GovernanceIssueApi = {
  item: (id: string) => Promise<unknown>;
};

const issueFirstDetectedCache = new Map<string, string>();
const issueFirstDetectedRequestCache = new Map<string, Promise<string>>();

function toStableKey(reference: unknown): string | undefined {
  if (!reference) {
    return undefined;
  }

  if (typeof reference === 'string') {
    const key = reference.trim();
    return key.length ? key : undefined;
  }

  if (typeof reference !== 'object') {
    return undefined;
  }

  const candidate = reference as {
    getStableKey?: () => string | undefined;
    stableKey?: string;
    key?: string;
  };

  const methodKey = candidate.getStableKey?.();
  if (typeof methodKey === 'string' && methodKey.trim().length > 0) {
    return methodKey;
  }

  if (typeof candidate.stableKey === 'string' && candidate.stableKey.trim().length > 0) {
    return candidate.stableKey;
  }

  if (typeof candidate.key === 'string' && candidate.key.trim().length > 0) {
    return candidate.key;
  }

  return undefined;
}

function toInternalId(value: unknown): string | undefined {
  if (!value) {
    return undefined;
  }

  if (typeof value === 'string') {
    const id = value.trim();
    return id.length ? id : undefined;
  }

  if (typeof value !== 'object') {
    return undefined;
  }

  const candidate = value as {
    getId?: () => string | undefined;
    id?: string;
  };

  const runtimeId = candidate.getId?.();
  if (typeof runtimeId === 'string' && runtimeId.trim().length > 0) {
    return runtimeId;
  }

  if (typeof candidate.id === 'string' && candidate.id.trim().length > 0) {
    return candidate.id;
  }

  return undefined;
}

/** Clamp a backend confidence to 0..1, or null when it carries no value. */
function toConfidenceValue(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }

  return Math.max(0, Math.min(1, value));
}

/** Narrow an unknown to a known `HealthConfidenceState`, tolerating absence on historical rows. */
function toConfidenceState(value: unknown): HealthConfidenceState | null {
  return value === HealthConfidenceState.Exact || value === HealthConfidenceState.Estimated || value === HealthConfidenceState.Unknown
    ? value
    : null;
}

function toExplanation(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
}

async function loadIssueFirstDetectedLabel(api: ISyncCoreApi | undefined, issueId: string): Promise<string> {
  const cacheKey = issueId.trim();
  if (!api || !cacheKey) {
    return '—';
  }

  const cached = issueFirstDetectedCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const pending = issueFirstDetectedRequestCache.get(cacheKey);
  if (pending) {
    return pending;
  }

  const governance = (api as unknown as { governance?: Record<string, unknown> }).governance;
  const maybeIssueApi = governance?.issue ?? governance?.issues;
  const issueApi =
    maybeIssueApi && typeof maybeIssueApi === 'object' && typeof (maybeIssueApi as GovernanceIssueApi).item === 'function'
      ? (maybeIssueApi as GovernanceIssueApi)
      : undefined;

  if (!issueApi) {
    return '—';
  }

  const request = issueApi
    .item(issueId)
    .then((issue) => {
      const candidate = issue as {
        createdAt?: unknown;
        firstDetectedAt?: unknown;
        detectedAt?: unknown;
        updatedAt?: unknown;
      };

      const dateValue = candidate.firstDetectedAt ?? candidate.detectedAt ?? candidate.createdAt ?? candidate.updatedAt;
      if (!dateValue) {
        return '—';
      }

      const timestamp = new Date(dateValue as string | number | Date).getTime();
      if (!Number.isFinite(timestamp)) {
        return '—';
      }

      return new Date(timestamp).toLocaleDateString();
    })
    .catch(() => '—')
    .finally(() => {
      issueFirstDetectedRequestCache.delete(cacheKey);
    });

  issueFirstDetectedRequestCache.set(cacheKey, request);

  const loadedLabel = await request;
  issueFirstDetectedCache.set(cacheKey, loadedLabel);
  return loadedLabel;
}

function toIssueTypeKeys(issue: unknown): string[] {
  if (!issue || typeof issue !== 'object') {
    return [];
  }

  const result = new Set<string>();
  const candidate = issue as {
    issueType?: unknown;
    issueTypes?: unknown;
    type?: unknown;
    types?: unknown;
  };

  const appendStableKey = (value: unknown) => {
    const key = toStableKey(value);
    if (key) {
      result.add(key);
    }
  };

  appendStableKey(candidate.issueType);
  appendStableKey(candidate.type);

  const appendArray = (value: unknown) => {
    if (!Array.isArray(value)) {
      return;
    }

    for (const entry of value) {
      appendStableKey(entry);
    }
  };

  appendArray(candidate.issueTypes);
  appendArray(candidate.types);

  return Array.from(result);
}

/** Pull the formula evidence off a loaded issue, tolerating runtime instances and plain DTOs. */
function toIssueEvidence(issue: unknown): GapIssueEvidence[] {
  if (!issue || typeof issue !== 'object') {
    return [];
  }

  const evidence = (issue as { evidence?: unknown }).evidence;
  if (!Array.isArray(evidence)) {
    return [];
  }

  return evidence
    .map((entry): GapIssueEvidence | undefined => {
      if (!entry || typeof entry !== 'object') {
        return undefined;
      }

      const candidate = entry as { path?: unknown; value?: unknown; reasoning?: unknown; isTruncated?: unknown };
      return {
        path: typeof candidate.path === 'string' ? candidate.path : undefined,
        value: candidate.value,
        reasoning: typeof candidate.reasoning === 'string' ? candidate.reasoning : undefined,
        isTruncated: typeof candidate.isTruncated === 'boolean' ? candidate.isTruncated : undefined,
      };
    })
    .filter((entry): entry is GapIssueEvidence => !!entry);
}

function IssueFirstDetectedReference({ api, issueId }: { api?: ISyncCoreApi; issueId?: string }) {
  const [label, setLabel] = useState<string>('—');

  useEffect(() => {
    if (!issueId) {
      setLabel('—');
      return;
    }

    let active = true;

    void (async () => {
      const loadedLabel = await loadIssueFirstDetectedLabel(api, issueId);
      if (!active) {
        return;
      }

      setLabel(loadedLabel);
    })();

    return () => {
      active = false;
    };
  }, [api, issueId]);

  return <>{label}</>;
}

function toAllHealthRows(item: ClientContentItemEntity | undefined): GapRow[] {
  if (!item) {
    return [];
  }

  const healthEntries = item.health || [];

  return healthEntries
    .map((entry): GapRow | undefined => {
      if (!entry || typeof entry !== 'object') {
        return undefined;
      }

      const score = typeof entry.score === 'number' && Number.isFinite(entry.score) ? entry.score : null;
      const healthCheckKey = toStableKey(entry.healthCheck);
      const healthCheckRevisionId = toRevisionId(entry.healthCheck);
      const issueId = toInternalId(entry.issueId);

      if (!healthCheckKey) {
        return undefined;
      }

      const candidate = entry as { confidence?: unknown; confidenceState?: unknown; explanation?: unknown };

      return {
        healthCheckKey,
        healthCheckRevisionId,
        score,
        issueId,
        confidence: toConfidenceValue(candidate.confidence),
        confidenceState: toConfidenceState(candidate.confidenceState),
        explanation: toExplanation(candidate.explanation),
      };
    })
    .filter((entry): entry is GapRow => !!entry)
    .sort((left, right) => {
      const leftHasScore = typeof left.score === 'number' && Number.isFinite(left.score);
      const rightHasScore = typeof right.score === 'number' && Number.isFinite(right.score);

      if (!leftHasScore && !rightHasScore) {
        return 0;
      }

      if (!leftHasScore) {
        return 1;
      }

      if (!rightHasScore) {
        return -1;
      }

      const leftScore = left.score as number;
      const rightScore = right.score as number;
      return rightScore - leftScore;
    });
}

/** The score below which a check is a gap of the page. */
const GAP_SCORE_BELOW = 0.9;

function toGapRows(rows: GapRow[]): GapRow[] {
  return rows.filter((entry) => typeof entry.score === 'number' && Number.isFinite(entry.score) && entry.score < GAP_SCORE_BELOW);
}

/** The revision a figure's check reference names, tolerating runtime references and plain DTOs. */
function toRevisionId(reference: unknown): string | undefined {
  if (!reference || typeof reference !== 'object') {
    return undefined;
  }

  const candidate = reference as { getRevisionId?: () => unknown; id?: unknown };
  const revisionId = candidate.getRevisionId?.() ?? candidate.id;
  return typeof revisionId === 'string' && revisionId.trim().length > 0 ? revisionId : undefined;
}

/**
 * The checks a page and its suggested version are read by, side by side: every check of the page, then every check
 * only the suggested version has a figure for, which reads as having none now.
 */
function toComparedRows({ current, suggested }: { current: GapRow[]; suggested: GapRow[] }): GapRow[] {
  const known = new Set(current.map((row) => row.healthCheckKey));
  const added = suggested
    .filter((row) => !known.has(row.healthCheckKey))
    .map(
      (row): GapRow => ({ healthCheckKey: row.healthCheckKey, score: null, confidence: null, confidenceState: null, explanation: null })
    );

  return [...current, ...Array.from(new Map(added.map((row) => [row.healthCheckKey, row])).values())];
}

export function ContentItemGapsTable(props: {
  api?: ISyncCoreApi;
  item?: ClientContentItemEntity;
  /** Called once an optimization started here has a draft, so the screen can show it for review. */
  onDraftReady?: () => void;
  /** A check a link named: its row is highlighted, expanded and, where Optimize can take it, pre-selected. */
  highlightHealthCheckKey?: string;
  /**
   * The page's suggested version, while it awaits its review: each check shows its health now beside its health in
   * the suggested version, as Sync Core measured it, and nothing is offered to optimize until the suggested version
   * is decided.
   */
  draft?: ClientContentItemEntity;
}) {
  const { api, item, onDraftReady, highlightHealthCheckKey, draft } = props;
  const offersOptimize = !draft;
  const [healthFilter, setHealthFilter] = useState<HealthFilter>('gaps');
  const [showWeights, setShowWeights] = useState<boolean>(readShowWeights);
  const [referenceByHealthCheckKey, setReferenceByHealthCheckKey] = useState<Record<string, HealthCheckReferenceData>>({});
  const [weightByRevisionId, setWeightByRevisionId] = useState<Record<string, HealthCheckRevisionWeight>>({});
  const [expandedHealthCheckKeys, setExpandedHealthCheckKeys] = useState<Set<string>>(() => new Set());
  const [selectedHealthCheckKeys, setSelectedHealthCheckKeys] = useState<string[]>([]);
  const [issueTypeKeysByIssueId, setIssueTypeKeysByIssueId] = useState<Record<string, string[]>>({});
  const [evidenceByIssueId, setEvidenceByIssueId] = useState<Record<string, GapIssueEvidence[]>>({});
  const [availableOptimizationTypes, setAvailableOptimizationTypes] = useState<ClientContentOptimizationTypeEntity[]>([]);
  const [optimizationSubmitting, setOptimizationSubmitting] = useState(false);
  // Read when the draft arrives rather than followed as a dependency, so a screen passing a new function
  // on every render does not restart the wait.
  const onDraftReadyRef = useRef(onDraftReady);
  onDraftReadyRef.current = onDraftReady;
  // The optimization the Optimize action started, followed until it has a draft or has ended.
  const optimization = useOptimizationFollow({
    api,
    onDraftReady: () => {
      setSelectedHealthCheckKeys([]);
      onDraftReadyRef.current?.();
    },
  });
  // The page the table shows now, for a create that answers after it changed.
  const itemIdRef = useRef(item?.id);
  itemIdRef.current = item?.id;

  const allHealthRows = useMemo(() => toAllHealthRows(item), [item]);
  // The suggested version's own measurement: a suggested version Sync Core has not measured carries no checks.
  const draftHealthRows = useMemo(() => toAllHealthRows(draft), [draft]);
  const draftMeasured = draftHealthRows.length > 0;
  // Each check's 0–1 score in the suggested version, as Sync Core stores it.
  const suggestedScoreByHealthCheckKey = useMemo(() => {
    const map: Record<string, number> = {};
    for (const row of draftHealthRows) {
      if (typeof row.score === 'number' && Number.isFinite(row.score) && !(row.healthCheckKey in map)) {
        map[row.healthCheckKey] = row.score;
      }
    }
    return map;
  }, [draftHealthRows]);
  // While a suggested version is compared, a check that only it has a figure for is listed too: it moves the
  // suggested version's health, so the reviewer sees it.
  const listedHealthRows = useMemo(
    () => (draft ? toComparedRows({ current: allHealthRows, suggested: draftHealthRows }) : allHealthRows),
    [draft, allHealthRows, draftHealthRows]
  );
  const allHealthCheckKeys = useMemo(() => Array.from(new Set(listedHealthRows.map((row) => row.healthCheckKey))), [listedHealthRows]);
  const allHealthCheckKeysSignature = useMemo(() => allHealthCheckKeys.join('|'), [allHealthCheckKeys]);

  useEffect(() => {
    setHealthFilter('gaps');
    setSelectedHealthCheckKeys([]);
    setIssueTypeKeysByIssueId({});
    setEvidenceByIssueId({});
    setAvailableOptimizationTypes([]);
    optimization.reset();
    setExpandedHealthCheckKeys(new Set());
  }, [item?.id]);

  useEffect(() => {
    if (!api) {
      setAvailableOptimizationTypes([]);
      return;
    }

    let active = true;

    void (async () => {
      try {
        const listed = await loadActiveOptimizationTypes(api);
        if (!active) {
          return;
        }

        setAvailableOptimizationTypes(listed);
      } catch {
        if (!active) {
          return;
        }

        setAvailableOptimizationTypes([]);
      }
    })();

    return () => {
      active = false;
    };
  }, [api]);

  const issueIdsSignature = useMemo(() => {
    const ids = allHealthRows
      .map((row) => row.issueId)
      .filter((issueId): issueId is string => !!issueId)
      .sort();
    return ids.join('|');
  }, [allHealthRows]);

  useEffect(() => {
    if (!api || !issueIdsSignature) {
      setIssueTypeKeysByIssueId({});
      setEvidenceByIssueId({});
      return;
    }

    let active = true;

    void (async () => {
      const governance = (api as unknown as { governance?: Record<string, unknown> }).governance;
      const maybeIssueApi = governance?.issue ?? governance?.issues;
      const issueApi =
        maybeIssueApi && typeof maybeIssueApi === 'object' && typeof (maybeIssueApi as GovernanceIssueApi).item === 'function'
          ? (maybeIssueApi as GovernanceIssueApi)
          : undefined;

      if (!issueApi) {
        if (active) {
          setIssueTypeKeysByIssueId({});
          setEvidenceByIssueId({});
        }
        return;
      }

      const issueIds = issueIdsSignature.split('|').filter((issueId) => issueId.length > 0);
      const entries = await Promise.all(
        issueIds.map(async (issueId): Promise<[string, { typeKeys: string[]; evidence: GapIssueEvidence[] }]> => {
          try {
            const issue = await issueApi.item(issueId);
            return [issueId, { typeKeys: toIssueTypeKeys(issue), evidence: toIssueEvidence(issue) }];
          } catch {
            return [issueId, { typeKeys: [], evidence: [] }];
          }
        })
      );

      if (!active) {
        return;
      }

      setIssueTypeKeysByIssueId(Object.fromEntries(entries.map(([issueId, value]) => [issueId, value.typeKeys])));
      setEvidenceByIssueId(Object.fromEntries(entries.map(([issueId, value]) => [issueId, value.evidence])));
    })();

    return () => {
      active = false;
    };
  }, [api, issueIdsSignature]);

  useEffect(() => {
    if (!api || !allHealthCheckKeys.length) {
      setReferenceByHealthCheckKey({});
      return;
    }

    let active = true;

    void (async () => {
      const entries = await Promise.all(
        allHealthCheckKeys.map(async (healthCheckKey): Promise<[string, HealthCheckReferenceData]> => {
          const referenceData = await loadHealthCheckReferenceDataByKey(api, healthCheckKey);
          return [healthCheckKey, referenceData];
        })
      );

      if (!active) {
        return;
      }

      setReferenceByHealthCheckKey(Object.fromEntries(entries));
    })();

    return () => {
      active = false;
    };
  }, [api, allHealthCheckKeysSignature]);

  // The revisions of the checks the page's figures were computed with, whose weights the health counts by.
  const scoredRevisionIds = useMemo(
    () =>
      Array.from(
        new Set(
          allHealthRows
            .filter(hasFiniteScore)
            .map((row) => row.healthCheckRevisionId)
            .filter((revisionId): revisionId is string => !!revisionId)
        )
      ),
    [allHealthRows]
  );
  const scoredRevisionIdsSignature = useMemo(() => scoredRevisionIds.join('|'), [scoredRevisionIds]);

  useEffect(() => {
    if (!api || !scoredRevisionIds.length) {
      setWeightByRevisionId({});
      return;
    }

    let active = true;
    setWeightByRevisionId({});

    // Each weight shows as soon as it is read: one that is slow keeps only its own row waiting.
    for (const revisionId of scoredRevisionIds) {
      void loadHealthCheckWeightByRevisionId(api, revisionId).then((weight) => {
        if (active) {
          setWeightByRevisionId((previous) => ({ ...previous, [revisionId]: weight }));
        }
      });
    }

    return () => {
      active = false;
    };
  }, [api, scoredRevisionIdsSignature]);

  const featureAssignmentLookup = useMemo(
    () => buildFeatureAssignmentLookup(item?.terms as GapTermsInput | undefined),
    [item?.terms, item?.id]
  );

  const issueIdByHealthCheckKey = useMemo(() => {
    const map: Record<string, string> = {};
    for (const row of allHealthRows) {
      if (row.issueId && !map[row.healthCheckKey]) {
        map[row.healthCheckKey] = row.issueId;
      }
    }
    return map;
  }, [allHealthRows]);

  const scorePercentageByHealthCheckKey = useMemo(() => {
    const map: Record<string, number> = {};
    for (const row of allHealthRows) {
      const scorePercentage = toScorePercentage(row.score);
      if (scorePercentage !== undefined && !(row.healthCheckKey in map)) {
        map[row.healthCheckKey] = scorePercentage;
      }
    }
    return map;
  }, [allHealthRows]);

  const explanationByHealthCheckKey = useMemo(() => {
    const map: Record<string, string> = {};
    for (const row of allHealthRows) {
      if (row.explanation && !(row.healthCheckKey in map)) {
        map[row.healthCheckKey] = row.explanation;
      }
    }
    return map;
  }, [allHealthRows]);

  const detailByHealthCheckKey = useMemo(() => {
    const map: Record<string, GapAssignmentDetail> = {};
    for (const healthCheckKey of allHealthCheckKeys) {
      const referenceData = referenceByHealthCheckKey[healthCheckKey];
      const issueId = issueIdByHealthCheckKey[healthCheckKey];
      map[healthCheckKey] = resolveGapAssignmentDetail({
        formula: referenceData?.formula,
        healthCheckName: referenceData?.name,
        healthCheckDescription: referenceData?.description,
        evidence: issueId ? evidenceByIssueId[issueId] : undefined,
        scorePercentage: scorePercentageByHealthCheckKey[healthCheckKey] ?? null,
        explanation: explanationByHealthCheckKey[healthCheckKey] ?? null,
        lookup: featureAssignmentLookup,
      });
    }
    return map;
  }, [
    allHealthCheckKeys,
    referenceByHealthCheckKey,
    featureAssignmentLookup,
    issueIdByHealthCheckKey,
    scorePercentageByHealthCheckKey,
    explanationByHealthCheckKey,
    evidenceByIssueId,
  ]);

  const toggleExpanded = (healthCheckKey: string) => {
    setExpandedHealthCheckKeys((previous) => {
      const next = new Set(previous);
      if (next.has(healthCheckKey)) {
        next.delete(healthCheckKey);
      } else {
        next.add(healthCheckKey);
      }
      return next;
    });
  };

  /**
   * Deliberately over ALL scored checks, not the filtered view: a check's share is of the whole page's health, so
   * a row's stated weight and gain never change with the filter tab. The Weight column and Potential Gain read the
   * same share, so the two never weigh a check differently.
   */
  const scoreShares = useMemo(
    () =>
      toHealthScoreShares({
        scoredHealthChecks: allHealthRows
          .filter(hasFiniteScore)
          .map((row) => ({ healthCheckKey: row.healthCheckKey, healthCheckRevisionId: row.healthCheckRevisionId })),
        weightByRevisionId,
      }),
    [allHealthRows, weightByRevisionId]
  );
  const scoreShareByHealthCheckKey = scoreShares.byHealthCheckKey;
  const largestScoreShare = useMemo(
    () => Math.max(0, ...Object.values(scoreShares.byHealthCheckKey).map((share) => (share.kind === 'share' ? share.share : 0))),
    [scoreShares]
  );

  // A check is a gap where the page falls short of it now or would in its suggested version, so a check the
  // suggested version makes worse is never filed under a healthy page.
  const gapRows = useMemo(() => {
    if (!draft) {
      return toGapRows(allHealthRows);
    }

    const gapKeys = new Set([...toGapRows(allHealthRows), ...toGapRows(draftHealthRows)].map((row) => row.healthCheckKey));
    return listedHealthRows.filter((row) => gapKeys.has(row.healthCheckKey));
  }, [draft, allHealthRows, draftHealthRows, listedHealthRows]);

  // The named check is revealed once per page load: its details open, and the filter widens when
  // the Gaps view would not hold it. A reader who closes it afterwards is not fought over it.
  const revealedHighlightRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!highlightHealthCheckKey || !allHealthRows.length) {
      return;
    }
    const revealKey = `${item?.id ?? ''}:${highlightHealthCheckKey}`;
    if (revealedHighlightRef.current === revealKey) {
      return;
    }
    const row = allHealthRows.find((candidate) => candidate.healthCheckKey === highlightHealthCheckKey);
    if (!row) {
      return;
    }
    revealedHighlightRef.current = revealKey;
    setExpandedHealthCheckKeys((previous) => new Set(previous).add(highlightHealthCheckKey));
    if (!toGapRows(allHealthRows).some((candidate) => candidate.healthCheckKey === highlightHealthCheckKey)) {
      setHealthFilter('all');
    }
  }, [allHealthRows, highlightHealthCheckKey, item?.id]);

  const applicableRows = useMemo(
    () => listedHealthRows.filter((row) => hasFiniteScore(row) || suggestedScoreByHealthCheckKey[row.healthCheckKey] !== undefined),
    [listedHealthRows, suggestedScoreByHealthCheckKey]
  );
  // While a measured suggested version is compared - the comparison the Changed view is for; a suggested version not
  // measured yet has nothing to compare. It stands while the suggested version is applied, as the comparison does.
  const compared = !!draft && draftMeasured;
  // The checks whose shown health the suggested version changes, better or worse - exactly the rows that show an
  // arrow: a check it has a figure for that the page has none for, or the other way round, changes too.
  const changedRows = useMemo(
    () =>
      compared
        ? listedHealthRows.filter(
            (row) => !isSameHealth({ current: row.score, suggested: suggestedScoreByHealthCheckKey[row.healthCheckKey] })
          )
        : [],
    [compared, listedHealthRows, suggestedScoreByHealthCheckKey]
  );
  const rowsByFilter = useMemo<Record<HealthFilter, GapRow[]>>(
    () => ({ gaps: gapRows, applicable: applicableRows, all: listedHealthRows, changed: changedRows }),
    [gapRows, applicableRows, listedHealthRows, changedRows]
  );
  const healthFilters = useMemo(() => (compared ? [...HEALTH_FILTERS, CHANGED_FILTER] : HEALTH_FILTERS), [compared]);
  // A choice of the Changed view ends with the comparison it belongs to, so the next one opens on the default view:
  // the effect resets the choice as the reset on a new page does; until it has, the render that follows the
  // comparison's end shows the default view rather than a view that no longer has a tab.
  const shownFilter: HealthFilter = healthFilter === 'changed' && !compared ? 'gaps' : healthFilter;
  useEffect(() => {
    if (!compared) {
      setHealthFilter((previous) => (previous === 'changed' ? 'gaps' : previous));
    }
  }, [compared]);
  const visibleRows = rowsByFilter[shownFilter];

  const optimizationTypeKeysByIssueTypeKey = useMemo(
    () => toOptimizationTypeKeysByIssueTypeKey(availableOptimizationTypes),
    [availableOptimizationTypes]
  );

  const applicableTypeKeysByHealthCheckKey = useMemo(() => {
    const map: Record<string, string[]> = {};

    for (const row of allHealthRows) {
      const scorePercentage = toScorePercentage(row.score);
      if (scorePercentage === undefined || scorePercentage >= 100) {
        map[row.healthCheckKey] = [];
        continue;
      }

      const issueTypeKeys = row.issueId ? issueTypeKeysByIssueId[row.issueId] || [] : [];
      const typeKeys = new Set<string>();

      for (const issueTypeKey of issueTypeKeys) {
        for (const typeKey of optimizationTypeKeysByIssueTypeKey[issueTypeKey] || []) {
          typeKeys.add(typeKey);
        }
      }

      map[row.healthCheckKey] = Array.from(typeKeys);
    }

    return map;
  }, [allHealthRows, issueTypeKeysByIssueId, optimizationTypeKeysByIssueTypeKey]);
  // Pre-selecting waits until it is known that Optimize can take the check, and happens once, so a
  // reader who deselects it is not fought over that either.
  const selectedHighlightRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!highlightHealthCheckKey) {
      return;
    }
    const selectKey = `${item?.id ?? ''}:${highlightHealthCheckKey}`;
    if (selectedHighlightRef.current === selectKey) {
      return;
    }
    const applicable = applicableTypeKeysByHealthCheckKey[highlightHealthCheckKey];
    if (!applicable?.length) {
      return;
    }
    selectedHighlightRef.current = selectKey;
    setSelectedHealthCheckKeys((previous) =>
      previous.includes(highlightHealthCheckKey) ? previous : [...previous, highlightHealthCheckKey]
    );
  }, [applicableTypeKeysByHealthCheckKey, highlightHealthCheckKey, item?.id]);

  const selectedRows = useMemo(() => {
    const selectedKeySet = new Set(selectedHealthCheckKeys);
    return allHealthRows.filter((row) => selectedKeySet.has(row.healthCheckKey));
  }, [allHealthRows, selectedHealthCheckKeys]);

  const selectedTypeKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const row of selectedRows) {
      for (const typeKey of applicableTypeKeysByHealthCheckKey[row.healthCheckKey] || []) {
        keys.add(typeKey);
      }
    }
    return Array.from(keys);
  }, [applicableTypeKeysByHealthCheckKey, selectedRows]);

  // How much fixing a check would raise the page's health: what it falls short by, at its share of the health; while
  // the weights are still loading, that it waits for them.
  const getPotentialGain = (row: GapRow): { label: string; waiting: boolean } => {
    const none = { label: '—', waiting: false };
    const share = scoreShareByHealthCheckKey[row.healthCheckKey];
    if (typeof row.score !== 'number' || !Number.isFinite(row.score) || row.score === 1) {
      return none;
    }
    if (share?.kind === 'loading') {
      return { label: NO_SHARE.loading.label, waiting: true };
    }
    if (share?.kind !== 'share') {
      return none;
    }

    const weightedPotentialGain = (1 - Math.max(0, Math.min(1, row.score))) * share.share;

    if (!Number.isFinite(weightedPotentialGain) || weightedPotentialGain <= 0) {
      return none;
    }

    const roundedGain = Math.round(weightedPotentialGain * 10) / 10;
    if (roundedGain <= 0) {
      return none;
    }

    return { label: `+${roundedGain}%`, waiting: false };
  };

  const startOptimization = async () => {
    if (!api || !item) {
      optimization.setError('Missing API or content item.');
      return;
    }

    if (!selectedRows.length) {
      return;
    }

    if (!selectedTypeKeys.length) {
      optimization.setError('No matching optimization type found for the selected checks.');
      return;
    }

    setOptimizationSubmitting(true);
    optimization.setError(undefined);
    optimization.setNotice(undefined);
    optimization.setProgress(undefined);

    try {
      const selectedTypes = optimizationTypesByKeys({ types: availableOptimizationTypes, typeKeys: selectedTypeKeys });

      if (!selectedTypes.length) {
        throw new Error('No optimization type could be loaded for the selected checks.');
      }

      // The create answers with the optimization it started, which is what the action follows from here —
      // unless the table shows another page by then, whose gaps it was not started from.
      const startedFor = item.id;
      const started = await createCheckOptimization(api, { page: item, types: selectedTypes });
      if (itemIdRef.current !== startedFor) {
        return;
      }
      if (!started) {
        throw new Error('Unable to start optimization.');
      }
      optimization.follow(started);
    } catch (error) {
      optimization.setError((error as Error)?.message || 'Unable to start optimization.');
    } finally {
      setOptimizationSubmitting(false);
    }
  };

  if (allHealthRows.length === 0) {
    return <div className="px-6 py-10 text-center text-sm font-semibold text-slate-500">No health checks for this item.</div>;
  }

  const columnCount = ALWAYS_SHOWN_COLUMNS + (offersOptimize ? 1 : 0) + (showWeights ? 1 : 0);
  // The potential gains are weighed by the shares whether the weights show or not, so what the shares leave out
  // is said either way.
  const leftOutLine = toLeftOutLine(scoreShares.leftOut);

  return (
    <div>
      {offersOptimize && selectedRows.length > 0 ? (
        <div className="border-b border-primary/15 bg-primary/[0.05] px-6 py-3 motion-safe:animate-bp-slide-down">
          <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
            <div className="text-sm font-semibold text-slate-900">
              {selectedRows.length} check{selectedRows.length === 1 ? '' : 's'} selected
            </div>
            <Button
              variant="primary"
              disabled={optimizationSubmitting || (!!optimization.optimizationId && !optimization.notice) || selectedTypeKeys.length === 0}
              onClick={() => {
                void startOptimization();
              }}
            >
              <span className="inline-flex items-center gap-2">
                {optimizationSubmitting ? <FontAwesomeIcon icon={faSpinner} spin /> : null}
                <span>Optimize</span>
                <FontAwesomeIcon icon={faArrowRight} />
              </span>
            </Button>
          </div>
          {optimization.error ? <div className="mt-2 text-sm text-danger">{optimization.error}</div> : null}
          {optimization.notice ? <div className="mt-2 text-sm text-slate-600">{optimization.notice}</div> : null}
          {optimization.progress ? (
            <div className="mt-3">
              <ProgressBar
                size="hairline"
                percent={optimization.progress.progress}
                title={optimization.progress.title}
                label={<span className="text-sm">{optimization.progress.title}</span>}
              />
            </div>
          ) : null}
        </div>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-3 px-6 pb-2 pt-4">
        {/* Where the frame is narrower than the views, they wrap onto a second line, so every view and its count
            stays in view: in a strip that scrolls sideways a view can stand wholly past the edge, with no cut to show
            it is there. */}
        <div
          className="inline-flex max-w-full flex-wrap items-center gap-0.5 rounded-xl bg-slate-100/90 p-1"
          role="tablist"
          aria-label="Filter health checks"
        >
          {healthFilters.map((filter) => {
            const active = shownFilter === filter.key;
            return (
              <button
                key={filter.key}
                type="button"
                role="tab"
                aria-selected={active}
                className={getBrandPresenceSubTabClassName(active, 'gap-1.5 whitespace-nowrap')}
                onClick={() => setHealthFilter(filter.key)}
              >
                {filter.label}
                <span className="text-xs font-medium text-slate-400 tabular-nums">{rowsByFilter[filter.key].length}</span>
              </button>
            );
          })}
        </div>
        {/* How much each check counts towards the page's health, on demand: read rarely, so off until asked for. */}
        <Form.Check
          type="switch"
          role="switch"
          id={`gaps-show-weights-${item?.id ?? 'page'}`}
          className="mb-0 text-sm text-slate-600"
          label="Show weights"
          checked={showWeights}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const show = event.target.checked;
            rememberShowWeights(show);
            setShowWeights(show);
          }}
        />
      </div>
      {showWeights || leftOutLine ? (
        <p className="mb-0 px-6 pb-2 text-xs text-slate-500">
          {[showWeights ? WEIGHT_HINT : undefined, leftOutLine].filter(Boolean).join(' ')}
        </p>
      ) : null}
      {/* The container measures its own width: where it is narrow, each check's first detection and potential
          gain read under its name instead of in columns of their own, so the table reads without scrolling
          sideways, and an opened check's detail is held to the width of the part in view. */}
      <div className="@container overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50/70">
              {offersOptimize ? <th className={`${BP_TABLE_HEAD_CELL_CLASSES} w-14`}>Select</th> : null}
              <th className={`${BP_TABLE_HEAD_CELL_WIDE_CLASSES}`}>Check</th>
              <th className={`${BP_TABLE_HEAD_CELL_WIDE_CLASSES} ${draft ? 'w-28 @3xl:w-56' : 'w-32'}`}>Health</th>
              {showWeights ? (
                <th className={`${BP_TABLE_HEAD_CELL_WIDE_CLASSES} hidden w-32 @3xl:table-cell`} title={WEIGHT_HINT}>
                  Weight
                </th>
              ) : null}
              <th className={`${BP_TABLE_HEAD_CELL_WIDE_CLASSES} hidden w-40 @3xl:table-cell`}>{FIRST_DETECTED_LABEL}</th>
              <th className={`${BP_TABLE_HEAD_CELL_WIDE_CLASSES} hidden w-40 text-right @3xl:table-cell`}>{POTENTIAL_GAIN_LABEL}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {!visibleRows.length ? (
              <tr>
                <td colSpan={columnCount} className="px-6 py-8 text-sm text-slate-500">
                  {shownFilter === 'gaps'
                    ? 'All checks are healthy. No gaps detected for this item.'
                    : shownFilter === 'applicable'
                      ? 'No checks with a score for this item.'
                      : shownFilter === 'changed'
                        ? 'No check changes in the suggested version.'
                        : 'No health checks for this item.'}
                </td>
              </tr>
            ) : (
              visibleRows.map((row) => {
                const hasScore = typeof row.score === 'number' && Number.isFinite(row.score);
                const scorePercentage = toScorePercentage(row.score) ?? 0;
                const potentialGain = getPotentialGain(row);
                const showSelection = hasScore && scorePercentage < 100;
                const applicableTypeKeys = applicableTypeKeysByHealthCheckKey[row.healthCheckKey] || [];
                const selectionDisabled = !showSelection || applicableTypeKeys.length === 0;
                const selected = selectedHealthCheckKeys.includes(row.healthCheckKey);
                const detail = detailByHealthCheckKey[row.healthCheckKey];
                const expanded = expandedHealthCheckKeys.has(row.healthCheckKey);
                const detailPanelId = `gap-details-${row.healthCheckKey}`;

                const highlighted = row.healthCheckKey === highlightHealthCheckKey;

                return (
                  <React.Fragment key={row.healthCheckKey}>
                    <tr
                      data-highlighted={highlighted || undefined}
                      className={`transition-colors duration-150 ${
                        highlighted ? 'bg-primary/[0.05] shadow-[inset_2px_0_0_0_#009ee3]' : 'hover:bg-slate-50/80'
                      }`}
                    >
                      {offersOptimize ? (
                        <td className="px-4 py-4 text-sm text-slate-600">
                          {showSelection ? (
                            <input
                              type="checkbox"
                              disabled={selectionDisabled}
                              checked={selected}
                              title={selectionDisabled ? 'No optimization type available for this issue.' : undefined}
                              onChange={() => {
                                if (selectionDisabled) {
                                  return;
                                }

                                setSelectedHealthCheckKeys((previous) =>
                                  previous.includes(row.healthCheckKey)
                                    ? previous.filter((healthCheckKey) => healthCheckKey !== row.healthCheckKey)
                                    : [...previous, row.healthCheckKey]
                                );
                              }}
                            />
                          ) : null}
                        </td>
                      ) : null}
                      <td className={`px-6 py-4 text-sm font-semibold ${hasScore ? 'text-slate-900' : 'text-slate-400'}`}>
                        <div className="flex flex-wrap items-center gap-2">
                          <HealthCheckNameReference api={api} healthCheckKey={row.healthCheckKey} fallback={row.healthCheckKey} />
                          {detail ? (
                            <GapConfidenceBadge
                              confidence={toConfidencePercentage(row.confidence)}
                              expanded={expanded}
                              controlsId={detailPanelId}
                              onToggle={() => toggleExpanded(row.healthCheckKey)}
                              tooltip={describeConfidenceState(row.confidenceState)}
                            />
                          ) : null}
                        </div>
                        {showWeights ? (
                          <HealthScoreShareLine share={hasScore ? scoreShareByHealthCheckKey[row.healthCheckKey] : undefined} />
                        ) : null}
                        <div className="mt-1.5 space-y-0.5 text-xs font-medium text-slate-500 @3xl:hidden">
                          <div>
                            {FIRST_DETECTED_LABEL}{' '}
                            <span className="text-slate-700">
                              <IssueFirstDetectedReference api={api} issueId={row.issueId} />
                            </span>
                          </div>
                          <div>
                            {POTENTIAL_GAIN_LABEL}{' '}
                            <span className="tabular-nums text-emerald-600">
                              <PotentialGainValue gain={potentialGain} />
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {draft ? (
                          <HealthComparisonCell
                            current={hasScore ? (row.score ?? undefined) : undefined}
                            suggested={suggestedScoreByHealthCheckKey[row.healthCheckKey]}
                            measured={draftMeasured}
                          />
                        ) : (
                          <HealthScoreCell score={hasScore ? scorePercentage : undefined} />
                        )}
                      </td>
                      {showWeights ? (
                        <td className="hidden px-6 py-4 @3xl:table-cell">
                          <HealthScoreShareCell
                            share={hasScore ? scoreShareByHealthCheckKey[row.healthCheckKey] : undefined}
                            largestShare={largestScoreShare}
                          />
                        </td>
                      ) : null}
                      <td className="hidden px-6 py-4 text-sm text-slate-600 @3xl:table-cell">
                        <IssueFirstDetectedReference api={api} issueId={row.issueId} />
                      </td>
                      <td className="hidden px-6 py-4 text-right text-sm font-semibold text-emerald-600 tabular-nums @3xl:table-cell">
                        <PotentialGainValue gain={potentialGain} />
                      </td>
                    </tr>
                    {detail ? (
                      <tr className="bg-slate-50/60">
                        <td colSpan={columnCount} className="p-0">
                          <Collapse in={expanded}>
                            {/* No wider than the part of the table in view, so the explanation wraps at the
                                width it is read at rather than at the width of every column. */}
                            <div className="max-w-[100cqw]">
                              <GapAssignmentDetails id={detailPanelId} detail={detail} />
                            </div>
                          </Collapse>
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
